import type { AdminNotificationTarget } from "../../api/admin";

interface Props {
  value: AdminNotificationTarget | null | undefined;
  onChange: (next: AdminNotificationTarget | null) => void;
  /** When true, the field is shown as "(use scope default)" — null target. */
  allowEmpty?: boolean;
  label?: string;
  helpText?: string;
}

interface ProviderInfo {
  type: string;
  label: string;
  available: boolean;
}

const PROVIDERS: ProviderInfo[] = [
  { type: "slack", label: "Slack channel", available: true },
  { type: "email", label: "Email (coming soon)", available: false },
  { type: "git_mr", label: "Git Merge Request (coming soon)", available: false },
  { type: "voice_call", label: "Voice call (coming soon)", available: false },
  { type: "ms_teams", label: "Microsoft Teams (coming soon)", available: false },
];

export function NotificationTargetEditor({
  value,
  onChange,
  allowEmpty = true,
  label = "Notification target",
  helpText,
}: Props) {
  const hasTarget = !!value;
  const type = value?.type ?? "slack";
  const provider = PROVIDERS.find((p) => p.type === type);

  return (
    <div>
      <label className="label">{label}</label>
      {helpText ? (
        <p className="text-xs text-ink-low mt-0.5 mb-1">{helpText}</p>
      ) : null}

      <div className="flex flex-col gap-2">
        {allowEmpty ? (
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="target-mode"
              checked={!hasTarget}
              onChange={() => onChange(null)}
            />
            <span>Use scope default</span>
          </label>
        ) : null}

        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="target-mode"
            checked={hasTarget}
            onChange={() =>
              onChange({ type: "slack", config: { channel_id: "" } })
            }
          />
          <span>Override</span>
        </label>

        {hasTarget ? (
          <div className="ml-5 flex flex-col gap-2">
            <select
              className="input"
              value={type}
              onChange={(e) => {
                const t = e.target.value;
                onChange({ type: t, config: {} });
              }}
            >
              {PROVIDERS.map((p) => (
                <option key={p.type} value={p.type} disabled={!p.available}>
                  {p.label}
                </option>
              ))}
            </select>

            {provider?.available && type === "slack" ? (
              <input
                type="text"
                className="input"
                placeholder="Slack channel id (e.g. C0123456789)"
                value={String(value?.config?.channel_id ?? "")}
                onChange={(e) =>
                  onChange({
                    type: "slack",
                    config: { channel_id: e.target.value },
                  })
                }
              />
            ) : (
              <p className="text-xs text-danger">
                Provider <code>{type}</code> is not yet implemented.
              </p>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
